/**
 * Migration preview service — wraps `GET /api/migrations/{id}/preview`.
 *
 * The endpoint returns the persisted dry-run preview report (remediation work-list + confidence)
 * produced by the Importer on the dry-run completion path. When no preview has been generated yet
 * the endpoint responds `404`; this service treats that as "no preview available" and resolves to
 * `null` so the caller can render a "run a dry-run first" empty state rather than an error.
 */

import { authenticatedFetch } from '../../utils/authenticatedFetch';
import { getV2AuthHeaders } from '../../utils/authHeaders';
import { getEnv } from '../../config/env';
import type { MigrationPreviewReport } from '../../types/migrationPreview';

/**
 * Fetches the dry-run preview report for a migration.
 * @returns the report, or `null` when no preview has been generated yet (HTTP 404).
 */
export async function getMigrationPreview(
    token: string,
    migrationId: string
): Promise<MigrationPreviewReport | null> {
    const baseUrl = getEnv().VITE_EDCI_API_URL;
    const url = `${baseUrl}/api/migrations/${encodeURIComponent(migrationId)}/preview`;

    const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: getV2AuthHeaders(token),
    });

    if (response.status === 404) {
        // No preview has been generated for this migration yet — not an error condition.
        return null;
    }

    if (!response.ok) {
        throw new Error(
            `Error fetching migration preview: ${response.status} ${response.statusText}`
        );
    }

    const data: MigrationPreviewReport = await response.json();
    return data;
}
